/**
 * Subject, Order topic and Order number on the contact forms
 * (snippets/contact-topic-fields.liquid). Order Related shows the two order fields and
 * makes them required, and the order number must contain a digit; any other subject hides
 * and disables them, so the browser neither checks nor sends them.
 *
 * Announces wl-contact:submit (the chosen subject and order topic) and wl-contact:invalid
 * (the names of the fields that stopped a send) for assets/analytics-posthog-funnel.js.
 * Never a value the shopper typed: no order number, email, name, phone or message.
 */
(function () {
  if (customElements.get('contact-topic-fields')) return;

  const NUMBER_MISSING = 'Please enter your order number.';
  const NUMBER_NO_DIGIT = 'Please enter your order number, for example #12345.';

  class ContactTopicFields extends HTMLElement {
    connectedCallback() {
      if (this.subject) return;
      this.subject = this.querySelector('[data-topic-subject]');
      this.topic = this.querySelector('[data-topic-order-topic]');
      this.number = this.querySelector('[data-topic-order-number]');
      this.error = this.querySelector('[data-topic-error]');
      this.form = this.closest('form');
      if (!this.subject || !this.topic || !this.number) return;

      this.subject.addEventListener('change', () => this.sync());
      this.number.addEventListener('input', () => {
        this.number.setCustomValidity('');
        this.showError('');
      });
      if (this.form) {
        // A send attempt is a click on a submit button (Enter in a field clicks the default
        // one too); only the invalid events that follow it count, not a script's own
        // checkValidity() call. invalid does not bubble, so both listen in the capture phase.
        // The click also re-syncs, so the fields always match the Subject the browser is
        // about to validate, and sets the order number's message only now: set earlier, it
        // made screen readers announce the empty field as invalid before anything was typed.
        this.form.addEventListener('click', (event) => {
          if (event.target.closest && event.target.closest('button[type="submit"], input[type="submit"]')) {
            this.sync();
            this.checkNumber();
            this.attempt = true;
            setTimeout(() => { this.attempt = false; }, 0);
          }
        }, true);
        this.form.addEventListener('invalid', (event) => this.onInvalid(event), true);
        this.form.addEventListener('submit', () => this.onSubmit());
      }
      // The selects carry autocomplete="off", so Chrome does not restore the Subject after
      // Back (it did so after this element had synced, with no change event, leaving the
      // order fields hidden). Re-syncing on pageshow backs that up for any browser that
      // restores anyway.
      window.addEventListener('pageshow', () => this.sync());
      this.sync();
    }

    get isOrder() {
      return this.subject.value === this.getAttribute('data-order-subject');
    }

    sync() {
      const on = this.isOrder;
      this.querySelectorAll('[data-topic-order]').forEach((field) => {
        field.hidden = !on;
        field.querySelectorAll('input, select').forEach((control) => {
          control.disabled = !on;
          control.required = on;
        });
      });
      if (!on) {
        this.number.setCustomValidity('');
        this.showError('');
      }
    }

    checkNumber() {
      const value = this.number.value.trim();
      let message = '';
      if (this.number.required) {
        if (!value) message = NUMBER_MISSING;
        else if (!/\d/.test(value)) message = NUMBER_NO_DIGIT;
      }
      this.number.setCustomValidity(message);
    }

    showError(message) {
      if (this.error) this.error.textContent = message;
    }

    onInvalid(event) {
      const control = event.target;
      if (control === this.number) this.showError(this.number.validationMessage);
      if (!this.attempt) return;
      // Every failing field fires its own invalid event during one send attempt; collect
      // their names and announce once.
      if (!this.failed) {
        this.failed = [];
        setTimeout(() => {
          const fields = this.failed;
          this.failed = null;
          this.announce('wl-contact:invalid', { fields: fields });
        }, 0);
      }
      const match = /^contact\[(.+)\]$/.exec(control.name || '');
      const field = match ? match[1].trim().toLowerCase().replace(/\s+/g, '_') : null;
      if (field && this.failed.indexOf(field) === -1) this.failed.push(field);
    }

    onSubmit() {
      this.announce('wl-contact:submit', {
        subject: this.subject.value || null,
        order_topic: this.isOrder ? this.topic.value || null : null
      });
    }

    announce(name, detail) {
      this.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail: Object.assign({ surface: this.getAttribute('data-surface') || null }, detail)
        })
      );
    }
  }

  customElements.define('contact-topic-fields', ContactTopicFields);
})();
